import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingCart, User, LogOut, Package, ChevronDown, MessageCircle, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import CatalogMenu from "@/components/layout/CatalogMenu";
import MobileNav from "@/components/layout/MobileNav";
import GlobalSearchModal from "@/components/GlobalSearchModal";
import logoAudrivio from "@/assets/logo-audrivio.png";
import { trackContact } from "@/lib/trackContact";

export default function Header() {
  const { count, setOpen } = useCart();
  const { lang, setLang, t } = useLang();
  const { customer, logout } = useCustomerAuth();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const isModK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isModK) {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-xd-cyan" : "text-slate-200 hover:text-white"}`;

  return (
    <>
      <div className="w-full bg-xd-dark2 text-slate-300 text-xs py-2 text-center border-b border-xd-darkBorder">
        <span data-testid="promo-banner">{t("header_promo")}</span>
      </div>
      <header className="sticky top-0 z-40 bg-xd-dark/95 backdrop-blur-md border-b border-xd-darkBorder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-24 sm:h-28 flex items-center justify-between gap-6">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 text-white group">
            <img
              src={logoAudrivio}
              alt="audrivio"
              className="h-20 sm:h-24 w-auto object-contain drop-shadow-[0_0_14px_rgba(23,199,214,0.4)] group-hover:drop-shadow-[0_0_22px_rgba(23,199,214,0.6)] transition"
            />
            <span className="sr-only">audrivio</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">{t("nav_home")}</NavLink>
            <CatalogMenu linkCls={linkCls} />
            <NavLink to="/accessori" className={linkCls} data-testid="nav-accessories">{t("nav_accessories")}</NavLink>
            <NavLink to="/blog" className={linkCls} data-testid="nav-blog">{t("nav_blog")}</NavLink>
            <NavLink to="/chi-siamo" className={linkCls} data-testid="nav-about">{t("nav_about")}</NavLink>
            <NavLink to="/contatti" className={linkCls} data-testid="nav-contact">{t("nav_contact")}</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="header-search-inline"
              aria-label={lang === "it" ? "Cerca marca, modello o prodotto" : lang === "de" ? "Marke, Modell oder Produkt suchen" : "Search brand, model or product"}
              className="hidden md:inline-flex items-center gap-2 h-10 w-[240px] lg:w-[300px] px-3 rounded-lg bg-xd-dark2/60 border border-xd-darkBorder text-slate-400 hover:text-slate-200 hover:border-xd-cyan/40 hover:bg-xd-dark2 transition-colors text-sm"
            >
              <Search className="w-4 h-4 shrink-0 text-xd-cyanHover" />
              <span className="flex-1 text-left truncate">{lang === "it" ? "Cerca marca, modello o prodotto…" : lang === "de" ? "Marke, Modell oder Produkt suchen…" : "Search brand, model or product…"}</span>
              <span className="hidden lg:inline-flex items-center gap-1 text-[10px] font-mono-tech text-slate-500 border border-xd-darkBorder rounded px-1.5 py-0.5">
                ⌘K
              </span>
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="header-search-btn"
              aria-label={lang === "en" ? "Open search" : "Apri ricerca"}
              className="md:hidden inline-flex items-center gap-2 text-slate-200 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-xd-dark2"
            >
              <Search className="w-5 h-5" />
            </button>
            <a
              href="https://wa.me/393511294525?text=Ciao%20audrivio%2C%20vorrei%20maggiori%20informazioni"
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-cta"
              onClick={() => trackContact("whatsapp", "header")}
              className="hidden md:inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold text-sm px-4 py-2.5 rounded-lg shadow-[0_0_18px_rgba(37,211,102,0.35)] hover:shadow-[0_0_24px_rgba(37,211,102,0.55)] transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              {t("header_wa_cta")}
            </a>
            <div className="hidden sm:block">
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger data-testid="lang-select" className="h-8 w-[74px] bg-xd-dark2 border-xd-darkBorder text-slate-200 text-xs font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-xd-dark2 border-xd-darkBorder text-slate-200">
                  <SelectItem value="it">IT · €</SelectItem>
                  <SelectItem value="en">EN · €</SelectItem>
                  <SelectItem value="de">DE · €</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="header-cart-btn"
              className="relative flex items-center gap-2 text-slate-200 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-xd-dark2"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">{t("header_cart")}</span>
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-xd-orange text-white text-[10px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            {customer ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" data-testid="header-account-btn" className="flex items-center gap-2 text-slate-200 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-xd-dark2">
                    <span className="w-7 h-7 rounded-full bg-gradient-to-br from-xd-cyan to-xd-cyanHover text-xd-dark flex items-center justify-center text-xs font-bold">
                      {(customer.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="hidden sm:inline text-sm max-w-[100px] truncate">{customer.name?.split(" ")[0] || "Account"}</span>
                    <ChevronDown className="w-3.5 h-3.5 hidden sm:inline" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{customer.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/account" data-testid="menu-account" className="cursor-pointer"><User className="w-4 h-4 mr-2" /> {t("header_account_my")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/account" data-testid="menu-orders" className="cursor-pointer"><Package className="w-4 h-4 mr-2" /> {t("header_account_orders")}</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-500 cursor-pointer"><LogOut className="w-4 h-4 mr-2" /> {t("header_account_logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="header-login-btn" className="hidden sm:inline-flex items-center gap-2 text-slate-200 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-xd-dark2 text-sm font-medium">
                <User className="w-4 h-4" /> {t("header_login")}
              </Link>
            )}
            <MobileNav />
          </div>
        </div>
      </header>
      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
